import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { RootState } from 'src/store/index';
import dayjs from 'src/libs/dayjs';
import { Pagination } from 'src/interfaces/general';

export type State = {
  loading: boolean;
  loadMore: {
    user_assignment_file_id: string;
    pagination?: Pagination;
  }[];
};

export const adapter = createEntityAdapter<Model.HomeworkComment>({
  selectId: (model) => model.id,
});

const { reducer, actions } = createSlice({
  name: 'homework_comment',
  initialState: adapter.getInitialState<State>({
    loading: false,
    loadMore: [],
  }),
  reducers: {
    updateEntities(state, action: PayloadAction<Model.HomeworkComment[]>) {
      adapter.upsertMany(state, action);
    },
    updateEntity(state, action: PayloadAction<Model.HomeworkComment>) {
      adapter.removeOne(state, 'placeholder');
      adapter.upsertOne(state, { ...action.payload });
    },
    removeEntity(state, action: PayloadAction<Model.HomeworkComment>) {
      adapter.removeOne(state, action.payload.id);
    },
    updateLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    createDummy(state, action: PayloadAction<Partial<Model.HomeworkComment>>) {
      adapter.upsertOne(state, {
        total_comments: 0,
        id: 'placeholder',
        // comments: [],
        created_at: '',
        parent_id: action.payload.parent_id || null,
        updated_at: '',
        user_id: action.payload.user_id!,
        class_topic_id: action.payload.class_topic_id!,
        user_assignment_file_id: action.payload.user_assignment_file_id!,
        message: action.payload.message!,
        user: action.payload.user!,
      });
    },
    updatePagination(
      state,
      action: PayloadAction<{
        user_assignment_file_id: string;
        pagination: Pagination;
      }>,
    ) {
      const loadmoreItem = state.loadMore.find(
        (l) =>
          l.user_assignment_file_id === action.payload.user_assignment_file_id,
      );

      if (loadmoreItem) {
        const index = state.loadMore.indexOf(loadmoreItem);
        state.loadMore[index] = action.payload;
      } else {
        state.loadMore.push(action.payload);
      }
    },
    increaseNumberOfComments(
      state,
      action: PayloadAction<Model.HomeworkComment>,
    ) {
      const { parent_id = '' } = action.payload;
      const currentComment = state.entities[parent_id!];

      if (currentComment) {
        currentComment.total_comments++;
      }
    },
    decreaseNumberOfComments(
      state,
      action: PayloadAction<Model.HomeworkComment>,
    ) {
      const { parent_id } = action.payload;
      const currentComment = state.entities[parent_id!];

      if (currentComment) {
        currentComment.total_comments--;
      }
    },
  },
});

export const selector = adapter.getSelectors(
  (state: RootState) => state.model.homework_comment,
);

export const selectHomeworkComments = createSelector(
  selector.selectAll,
  (state: RootState, class_topic_id?: string) => class_topic_id,
  (
    state: RootState,
    class_topic_id?: string,
    user_assignment_file_id?: string,
  ) => user_assignment_file_id,
  (comments, class_topic_id, user_assignment_file_id) => {
    const results = comments.filter((c) => c.class_topic_id === class_topic_id);

    return results.sort((a, b) =>
      dayjs(a.created_at).isBefore(dayjs(b.created_at)) ? -1 : 1,
    );
  },
);

export const selectLoadMoreInfo = createSelector(
  (state: RootState) => state.model.homework_comment.loadMore,
  (state: RootState, user_assignment_file_id: string) =>
    user_assignment_file_id,
  (loadmores, user_assignment_file_id) => {
    const loadmoreItem = loadmores.find(
      (l) => l.user_assignment_file_id === user_assignment_file_id,
    );

    if (loadmoreItem) {
      const {
        next_page,
        current_page = 0,
        total_pages = 0,
        total_items = 0,
      } = loadmoreItem.pagination || {};

      return {
        hasLoadMore: next_page !== null || current_page < total_pages,
        total: total_items,
      };
    }

    return {
      hasLoadMore: false,
      total: 0,
    };
  },
);

export { reducer };
export default actions;
